import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

import { forwardRef, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import Carousel from 'react-slick';

import { legacyTheme, styled } from '@prose-ui/legacy';
import { makeStyles } from 'legacyStyles';

import TwoUp from 'Components/LegacyTwoUp';
import DesktopCarousel from 'Components/Slider';
import { breakpoints } from 'Components/ThemeProvider';

import { srcOrNextImageProp } from 'PropTypes/imageProps';

import useMedia from 'utils/useMedia';

import BlockLayout from './BlockLayout';

const MobileCarousel = styled(Carousel)`
  width: ${props => (props?.isMobile ? '100%' : '499px')};
  margin-top: ${props => (props?.isMobile ? 0 : '4.5rem')};
  margin-right: auto;
  margin-left: auto;

  .slick-slide {
    position: relative;
  }

  .slick-prev {
    left: 10px;
    z-index: 1;
    ::before {
      color: black;
    }
  }
  .slick-next {
    right: 20px;
    ::before {
      color: black;
    }
  }

  .slick-list {
    height: ${props =>
      props?.isMobile && props?.imageHeight
        ? `${props.imageHeight}px`
        : props?.isMobile
        ? '425px'
        : '566px'};
  }

  .slick-dots {
    li {
      width: 12px;
      margin: 0;

      button {
        width: 12px;
      }

      button::before {
        width: 12px;
        font-size: 7px;
      }
    }
  }

  ${legacyTheme.breakpoints.up('md')} {
    .slick-prev {
      left: -10px;
    }
  }

  ${legacyTheme.breakpoints.up('lg')} {
    width: 90%;
    margin-left: 50px;
  }
`;

const MobileImage = styled.img`
  width: 100%;
  object-fit: ${props => (props?.isCover ? 'cover' : 'fill')};
  height: ${props =>
    props?.isMobile && props?.imageHeight
      ? `${props.imageHeight}px`
      : props?.isMobile
      ? '425px'
      : '566px'};
`;

const DesktopImage = styled.img`
  display: block;
  object-fit: cover;
  width: 100%;
  max-width: ${legacyTheme.width.half};
  margin-left: auto;
  margin-right: auto;
`;

const useStyles = makeStyles({
  root: {
    alignItems: 'center',
  },
  slider: {
    width: 'fit-content',
    margin: 'auto',
    marginTop: 0,

    [legacyTheme.breakpoints.up('sm')]: {
      marginTop: '4.5rem',
    },

    [legacyTheme.breakpoints.up('md')]: {
      marginTop: 0,
    },
  },
  content: {
    [legacyTheme.breakpoints.match('md')]: {
      marginTop: '2rem',
    },
  },
  sliderMargin: {
    width: 'fit-content',
    margin: 'auto',
    marginTop: 0,
    top: 130,
  },
});

const SliderAndContent = forwardRef(
  (
    {
      children,
      sliderDataFrom,
      slides,
      withBackgroundControls,
      textPadding,
      isCover,
      carouselIndex,
      focusOnSelect,
      onIndexChange,
      blockLayoutMargin,
      ...props
    },
    ref
  ) => {
    const carouselRef = useRef();
    const isMobile = useMedia(`(max-width: ${breakpoints.sm - 1}px)`);
    const isTablet = useMedia(`(max-width: ${breakpoints.md - 1}px)`);
    const { classes } = useStyles();
    const [imageHeight, setImageHeight] = useState(Math.round(window?.innerWidth * 1.13));

    // Recompute image's height on browser's resize
    useEffect(() => {
      const updateImageHeight = () => {
        setImageHeight(Math.round(window?.innerWidth * 1.13));
      };

      window.addEventListener('resize', updateImageHeight);

      return () => {
        window.removeEventListener('resize', updateImageHeight);
      };
    }, []);

    useEffect(() => {
      carouselRef?.current?.slickGoTo(carouselIndex);
    }, [carouselIndex]);

    return (
      <TwoUp ref={ref} classes={{ root: !withBackgroundControls ? classes.root : '' }} {...props}>
        {/* Left block - Carousel */}
        {isTablet ? (
          <MobileCarousel
            ref={carouselRef}
            afterChange={index => onIndexChange?.(index)}
            arrows={false}
            dots
            focusOnSelect={focusOnSelect}
            imageHeight={imageHeight}
            infinite
            isMobile={isMobile}
            slidesToScroll={1}
            slidesToShow={1}
            speed={500}
          >
            {slides.map(({ alt, image }, i) => (
              <div key={String(i)} data-click="change-slide" data-from={sliderDataFrom}>
                <MobileImage
                  alt={alt}
                  imageHeight={imageHeight}
                  isCover={isCover}
                  isMobile={isMobile}
                  src={image.src}
                />
              </div>
            ))}
          </MobileCarousel>
        ) : (
          <DesktopCarousel
            classes={{ root: !withBackgroundControls ? classes.slider : classes.sliderMargin }}
            controlsTheme="noir"
            dataFrom={sliderDataFrom}
            onIndexChange={onIndexChange}
            parentIndex={carouselIndex}
            withBackgroundControls={withBackgroundControls}
          >
            {slides.map(({ alt, image }, i) => (
              <DesktopImage key={String(i)} alt={alt} src={image.src} />
            ))}
          </DesktopCarousel>
        )}

        {/* Right block - Text */}
        <BlockLayout
          blockLayoutMargin={blockLayoutMargin}
          classes={{ content: classes.content }}
          contentWidth="half"
          textPadding={textPadding}
        >
          {children}
        </BlockLayout>
      </TwoUp>
    );
  }
);

SliderAndContent.displayName = 'SliderAndContent';

SliderAndContent.propTypes = {
  children: PropTypes.node.isRequired,
  /** Data attribute indicating navigation provenance */
  sliderDataFrom: PropTypes.string,
  /** Images to display. */
  slides: PropTypes.arrayOf(
    PropTypes.shape({
      alt: PropTypes.string.isRequired,
      image: srcOrNextImageProp.isRequired,
    })
  ).isRequired,
  withBackgroundControls: PropTypes.bool,
  textPadding: PropTypes.number,
  isCover: PropTypes.bool,
  carouselIndex: PropTypes.number,
  focusOnSelect: PropTypes.bool,
  onIndexChange: PropTypes.func,
  blockLayoutMargin: PropTypes.string,
};

SliderAndContent.defaultProps = {
  onIndexChange: null,
  carouselIndex: 0,
  withBackgroundControls: false,
  textPadding: 24,
  isCover: false,
  sliderDataFrom: '',
  focusOnSelect: true,
  blockLayoutMargin: null,
};

export default SliderAndContent;
